import { withAppServerSideProps } from '@app/lib/withAppServerSideProps';
import { Box, Stack, Text } from '@redbubble/design-system';
import { FormattedMessage } from '@redbubble/next/intl';
import type { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import {
  RSPageType,
  useRSPageView,
} from '@redbubble/next-provider/rudderstack';
import type { NextPage } from 'next';
import getConfig from 'next/config';
import React, { useEffect, useRef } from 'react';

import styles from './styles.module.scss';

export const CookieInformation: NextPage = () => {
  useRSPageView(RSPageType.CookiePolicy);
  const [{ locale }] = useUserInfo<UserInfo>();
  const { publicRuntimeConfig } = getConfig();
  const declarationRef = useRef<HTMLDivElement>(null);

  const domainGroupId = publicRuntimeConfig.cookiebot?.domainGroupId;
  if (!domainGroupId) {
    throw new Error('Cookie Declaration script missing domainGroupId');
  }

  /**
   * The Cookie Declaration script has to be loaded manually as next/script will move
   * the script to the end of the page, injecting the content below the Footer. Using
   * the approach ensures we can inject it at the declarationRef document location.
   *
   * The script also has to match the current locale settings of the site as encoded
   * into the URL path. Cookiebot support recommends using the custom script at this
   * support article:
   *
   * https://support.cookiebot.com/hc/en-us/articles/360010146780-Changing-the-banner-language-based-on-the-URL
   *
   * However, since we already have the locale available via the useUserInfo hook, its
   * easier to just set the dataset.culture attribute directly. This results in less JS
   * to execute and avoids a number of script formatting issues.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  useEffect(() => {
    const script = window.document.createElement('script');
    script.src = `https://consent.cookiebot.com/${domainGroupId}/cd.js`;
    script.id = 'CookieDeclaration';
    script.async = true;
    script.type = 'text/javascript';
    script.dataset.culture = locale;
    declarationRef?.current?.appendChild(script);
  }, [declarationRef, domainGroupId, locale]);

  return (
    <Box display="flex" justifyContent="center">
      <Box
        paddingX="m"
        paddingY="xxxl"
        element="main"
        className={styles.pageMain}
      >
        <Stack paddingX="s" spacing="xl">
          <Text type="display1" element="h1">
            <FormattedMessage defaultMessage="Cookie policy" />
          </Text>
          <div ref={declarationRef} />
        </Stack>
      </Box>
    </Box>
  );
};

export const getServerSideProps = withAppServerSideProps();

export default CookieInformation;
